import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CommonDocumentHeader from "../../../components/Document/commonDocumentHeader";
import DependencyAnalysisResult from "../../../components/ReverseEngineering/DependencyAnalysisResult";
import ERDiagram from "../../../components/ReverseEngineering/ERDiagram";
import RequirementsAnalysisResult from "../../../components/ReverseEngineering/RequirementsAnalysisResult";
import CommonTabs from "../../../components/ui/tabs";
import { TOAST_MESSAGES } from "../../../constants/messages";
import { ReverseApi } from "../../../functions/api/reverse";
import ContextMenu from "../../../images/context_menu.svg?react";
import type {
  ReverseEngineeringDocument,
  ReverseEngineeringReportDetail,
} from "../../../interface/reverse";
import { setCurrentIds, setReportData } from "../../../slices/reverseEngineeringSlice";
import type { RootState } from "../../../store";
import styles from "./index.module.scss";

const ReverseEngineeringDetail = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentProject, currentUser } = useSelector((state: RootState) => state.auth);
  const { reportData } = useSelector((state: RootState) => state.reverseEngineering);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [documents, setDocuments] = useState<ReverseEngineeringDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [isAnyTabEditing, setIsAnyTabEditing] = useState(false);

  const fetchReportData = useCallback(async () => {
    if (!reportId || !currentProject?.id || !currentProject?.organization_id) {
      return;
    }

    setIsLoading(true);
    try {
      const reverseApi = new ReverseApi();
      const data = await reverseApi.getReverseEngineeringReport(
        currentProject.organization_id,
        currentProject.id,
        reportId,
      );

      if (data) {
        dispatch(setReportData(data));
        dispatch(setCurrentIds({ reportId, projectId: currentProject.id }));
        console.log("Fetched report data:", data);

        // Fetch documents for this report
        const docs = await reverseApi.getReverseEngineeringDocuments(
          currentProject.organization_id,
          currentProject.id,
          reportId,
        );
        if (docs) {
          setDocuments(docs);
        }
      } else {
        toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.REPORT_NOT_FOUND);
        navigate(-1);
      }
    } catch (error) {
      console.error("Failed to fetch report data:", error);
      toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.FETCH_REPORT_FAILED);
      navigate(-1);
    } finally {
      setIsLoading(false);
    }
  }, [reportId, currentProject?.id, currentProject?.organization_id, navigate, dispatch]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  // Close context menu when clicking outside
  useEffect(() => {
    if (!showContextMenu) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[class*="contextMenuWrapper"]')) {
        setShowContextMenu(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showContextMenu]);

  const handleHistoryClick = () => {
    // TODO: Navigate to history page
    toast.info(TOAST_MESSAGES.REVERSE_ENGINEERING.HISTORY_PREPARING);
  };

  const handleEditTitle = () => {
    const dateValue = reportData?.createdAt ?? reportData?.created_at;
    const dateStr = dateValue
      ? new Date(dateValue).toLocaleDateString("ja-JP").replace(/\//g, "")
      : new Date().toLocaleDateString("ja-JP").replace(/\//g, "");
    setEditedTitle(reportData?.name || `分析レポート_${dateStr}`);
    setIsEditingTitle(true);
    setShowContextMenu(false);
  };

  const handleSaveTitle = async () => {
    if (!currentProject?.id || !currentProject?.organization_id || !reportId) {
      toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.PROJECT_INFO_MISSING);
      return;
    }

    if (!editedTitle.trim()) {
      toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.REPORT_NAME_REQUIRED);
      return;
    }

    try {
      const reverseApi = new ReverseApi();

      if (!currentUser?.id) {
        toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.USER_INFO_MISSING);
        return;
      }

      const success = await reverseApi.updateReverseEngineeringReportName(
        currentProject.organization_id,
        currentProject.id,
        reportId,
        editedTitle.trim(),
      );

      if (success && reportData?.id) {
        // Update local state
        const updatedReportData: ReverseEngineeringReportDetail = {
          ...reportData,
          id: reportData.id,
          name: editedTitle.trim(),
        };
        dispatch(setReportData(updatedReportData));
        setIsEditingTitle(false);
        toast.success(TOAST_MESSAGES.REVERSE_ENGINEERING.REPORT_NAME_UPDATED);
      }
    } catch (error) {
      console.error("Failed to update report name:", error);
      toast.error(TOAST_MESSAGES.REVERSE_ENGINEERING.REPORT_NAME_UPDATE_FAILED);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingTitle(false);
    setEditedTitle("");
  };

  const handleTabSelect = useCallback((index: number) => {
    setSelectedIndex(index);
  }, []);

  // Parse documents by type - moved before hooks to avoid conditional hook calls
  const dependencyDocument = documents.find((doc) => doc.document_type === "dependency-overview");
  const requirementsDocument = documents.find(
    (doc) => doc.document_type === "requirement-analysis",
  );
  const erDocument = documents.find((doc) => doc.document_type === "er-diagram");

  // Helper function to parse Python-style JSON - memoized to prevent dependency changes
  const parsePythonJSON = useCallback((content: string) => {
    if (!content) return null;

    // If already an object, return it
    if (typeof content === "object") return content;

    try {
      // First try parsing as-is
      return JSON.parse(content);
    } catch (_firstError) {
      try {
        let jsonStr = content;

        console.log("Starting parsePythonJSON, data size:", jsonStr.length);
        // Check data size and handle large data differently
        const dataSize = jsonStr.length;

        // Step 1: Always do basic Python to JSON replacements first
        jsonStr = jsonStr
          .replace(/\bNone\b/g, "null")
          .replace(/\bTrue\b/g, "true")
          .replace(/\bFalse\b/g, "false");

        // For very large data (> 250KB), try a different approach
        if (dataSize > 250000 || jsonStr.startsWith("{")) {
          console.warn("Large data or Python dict detected, attempting special processing");

          try {
            // First, convert the entire Python dict to proper JSON format
            // This is more reliable for Python dict format
            const cleanedJson = jsonStr;

            // Since this is Python dict format, use a more robust approach
            try {
              // Replace Python literals with JSON equivalents
              let pythonToJson = cleanedJson;

              // Step 1: Replace Python None, True, False
              pythonToJson = pythonToJson
                .replace(/\bNone\b/g, "null")
                .replace(/\bTrue\b/g, "true")
                .replace(/\bFalse\b/g, "false");

              // Pre-process: Escape backslashes that are not part of escape sequences
              // This prevents issues with \xa0, \u3000, etc.
              pythonToJson = pythonToJson
                .replace(/\\x([0-9a-fA-F]{2})/g, "\\u00$1") // Convert \xXX to \u00XX
                .replace(/\\u([0-9a-fA-F]{4})/g, "\\u$1"); // Keep Unicode escapes as is

              // Step 2: Convert Python dict to JSON format
              // Use a state machine approach to handle quotes properly
              let result = "";
              let i = 0;
              let inString = false;
              let stringDelimiter = "";

              // Track if we're inside a nested string (SQL queries, etc.)
              let bracketDepth = 0; // Track parentheses in SQL-like expressions

              while (i < pythonToJson.length) {
                const char = pythonToJson[i];

                if (!inString) {
                  // Check for string start
                  if (char === '"' || char === "'") {
                    inString = true;
                    stringDelimiter = char;
                    result += '"';
                    bracketDepth = 0; // Reset bracket depth when entering string
                  } else {
                    result += char;
                  }
                } else {
                  // We're inside a string
                  if (char === "\\" && i + 1 < pythonToJson.length) {
                    const nextChar = pythonToJson[i + 1];

                    // Handle Unicode escape sequences properly
                    if (nextChar === "u" && i + 5 < pythonToJson.length) {
                      // Unicode escape \uXXXX
                      result += pythonToJson.substring(i, i + 6);
                      i += 5;
                    } else if (nextChar === "x" && i + 3 < pythonToJson.length) {
                      // Hex escape \xXX
                      result += pythonToJson.substring(i, i + 4);
                      i += 3;
                    } else if (nextChar === "n") {
                      result += "\\n";
                      i++;
                    } else if (nextChar === "r") {
                      result += "\\r";
                      i++;
                    } else if (nextChar === "t") {
                      result += "\\t";
                      i++;
                    } else if (nextChar === "\\") {
                      result += "\\\\";
                      i++;
                    } else if (nextChar === stringDelimiter) {
                      // Escaped quote - properly handled
                      result += stringDelimiter === '"' ? '\\"' : "'";
                      i++;
                    } else if (nextChar === '"' || nextChar === "'") {
                      // Escaped quote - always escape in JSON
                      result += nextChar === '"' ? '\\"' : "'";
                      i++;
                    } else {
                      // Unknown escape - keep both characters
                      result += char + nextChar;
                      i++;
                    }
                  } else if (char === stringDelimiter) {
                    // Check if this is really the end of the string
                    // Look ahead to see if we're in a SQL-like expression
                    const prevChars = pythonToJson.substring(Math.max(0, i - 10), i);

                    // Check if we're in a SQL IN clause or similar construct
                    if (
                      (prevChars.includes(" in (") || prevChars.includes(" IN (")) &&
                      bracketDepth > 0
                    ) {
                      // This is likely a quote inside a SQL expression, escape it
                      result += '\\"';
                    } else {
                      // End of string
                      inString = false;
                      stringDelimiter = "";
                      result += '"';
                    }
                  } else if (char === '"') {
                    // Always escape double quotes inside strings in JSON
                    result += '\\"';
                  } else if (char === "(" && stringDelimiter === "'") {
                    // Track parentheses depth for SQL-like expressions
                    bracketDepth++;
                    result += char;
                  } else if (char === ")" && stringDelimiter === "'") {
                    bracketDepth = Math.max(0, bracketDepth - 1);
                    result += char;
                  } else {
                    result += char;
                  }
                }
                i++;
              }

              try {
                const parsed = JSON.parse(result);
                console.log("Successfully parsed Python dict using state machine");

                if (parsed.selectedApps && parsed.risks) {
                  console.log(
                    "Data validated: apps =",
                    parsed.selectedApps.length,
                    ", risks =",
                    parsed.risks.length,
                  );
                  return parsed;
                }
              } catch (e: any) {
                console.error("State machine parsing failed:", e.message);

                // Extract position from error message
                const posMatch = e.message.match(/position (\d+)/);
                if (posMatch) {
                  const errorPos = parseInt(posMatch[1], 10);
                  const contextSize = 100;
                  const start = Math.max(0, errorPos - contextSize);
                  const end = Math.min(result.length, errorPos + contextSize);

                  console.error("BEFORE:", result.substring(start, errorPos));
                  console.error(">>> ERROR HERE <<<");
                  console.error("AFTER:", result.substring(errorPos, end));
                }
              }
            } catch (e) {
              console.log("Python dict conversion error:", e);
            }

            // If all parsing attempts failed, return default structure
            console.error("All parsing attempts failed, returning default structure");
            return {
              selectedApps: [],
              summary: {
                referencingApps: 0,
                referencedApps: 0,
                detectedRelations: 0,
                riskCount: 0,
                codeFileCount: 0,
                obfuscatedFileCount: 0,
              },
              risks: [],
              dependencies: [],
              app_info_data: {},
            };
          } catch (e) {
            console.error("Error during data processing:", e);
            // Return default structure
            return {
              selectedApps: [],
              summary: {
                referencingApps: 0,
                referencedApps: 0,
                detectedRelations: 0,
                riskCount: 0,
                codeFileCount: 0,
                obfuscatedFileCount: 0,
              },
              risks: [],
              dependencies: [],
              app_info_data: {},
            };
          }
        }

        // Final attempt to parse
        try {
          const parsed = JSON.parse(jsonStr);
          console.log("Successfully parsed JSON after conversion");
          return parsed;
        } catch (parseError) {
          // If parsing still fails, try to extract what we can
          console.warn("JSON parse failed, attempting to extract sections manually");

          const extractedData: any = {
            selectedApps: [],
            summary: {
              referencingApps: 0,
              referencedApps: 0,
              detectedRelations: 0,
              riskCount: 0,
              codeFileCount: 0,
              obfuscatedFileCount: 0,
            },
            risks: [],
            dependencies: [],
            app_info_data: {},
          };

          // Try to extract risks even if main parse fails
          try {
            const risksMatch = jsonStr.match(/"risks"\s*:\s*\[([\s\S]*?)\]/);
            if (risksMatch?.[1]) {
              const riskPattern = /\{[^{}]*?"level"\s*:\s*"(HIGH|MEDIUM|LOW)"[^{}]*?\}/gi;
              const riskMatches = risksMatch[1].match(riskPattern);
              if (riskMatches) {
                extractedData.risks = riskMatches
                  .map((riskStr) => {
                    const levelMatch = riskStr.match(/"level"\s*:\s*"(HIGH|MEDIUM|LOW)"/i);
                    const titleMatch = riskStr.match(/"title"\s*:\s*"([^"]+)"/);
                    const reasonMatch = riskStr.match(/"reason"\s*:\s*"([^"]+)"/);
                    const recommendationMatch = riskStr.match(/"recommendation"\s*:\s*"([^"]+)"/);

                    if (levelMatch && titleMatch) {
                      return {
                        level: levelMatch[1],
                        title: titleMatch[1],
                        reason: reasonMatch ? reasonMatch[1] : "",
                        recommendation: recommendationMatch ? recommendationMatch[1] : "",
                      };
                    }
                    return null;
                  })
                  .filter((r) => r !== null);
                console.log(`Extracted ${extractedData.risks.length} risks from failed parse`);
              }
            }
          } catch (_e) {
            console.error("Could not extract risks from failed parse");
          }

          // Try to extract dependencies
          try {
            const depsMatch = jsonStr.match(/"dependencies"\s*:\s*\[([\s\S]*?)\]/);
            if (depsMatch?.[1]) {
              const depPattern = /\{[^{}]*?"impact"\s*:\s*"(HIGH|MEDIUM|LOW)"[^{}]*?\}/gi;
              const depMatches = depsMatch[1].match(depPattern);
              if (depMatches) {
                extractedData.dependencies = depMatches
                  .map((depStr) => {
                    const impactMatch = depStr.match(/"impact"\s*:\s*"(HIGH|MEDIUM|LOW)"/i);
                    const typeMatch = depStr.match(/"type"\s*:\s*"([^"]+)"/);
                    const sourceMatch = depStr.match(/"source"\s*:\s*"([^"]+)"/);
                    const keyMatch = depStr.match(/"key"\s*:\s*"([^"]+)"/);
                    const affectedFieldsMatch = depStr.match(/"affectedFields"\s*:\s*(\d+)/);
                    const reasonMatch = depStr.match(/"reason"\s*:\s*"([^"]+)"/);

                    if (impactMatch) {
                      return {
                        impact: impactMatch[1] ?? "",
                        type: typeMatch?.[1] ?? "",
                        source: sourceMatch?.[1] ?? "",
                        key: keyMatch?.[1] ?? "",
                        affectedFields: affectedFieldsMatch?.[1]
                          ? parseInt(affectedFieldsMatch[1], 10)
                          : 0,
                        reason: reasonMatch?.[1] ?? "",
                      };
                    }
                    return null;
                  })
                  .filter((d) => d !== null);
                console.log(
                  `Extracted ${extractedData.dependencies.length} dependencies from failed parse`,
                );
              }
            }
          } catch (_e) {
            console.error("Could not extract dependencies from failed parse");
          }

          throw parseError; // Re-throw to go to the final fallback
        }
      } catch (secondError) {
        console.error("Failed to parse Python JSON even after repair attempt");
        console.error("Error:", (secondError as Error).message);

        // Log the problematic part of JSON for debugging
        const errorMatch = (secondError as Error).message.match(/position (\d+)/);
        if (errorMatch?.[1]) {
          const position = parseInt(errorMatch[1], 10);
          const start = Math.max(0, position - 50);
          const end = Math.min(content.length, position + 50);
          console.error("Problematic JSON section:", content.substring(start, end));
          console.error("Character at error position:", content[position]);
        }

        // For large data, try to extract essential parts only
        const defaultData: {
          selectedApps: unknown[];
          summary: {
            referencingApps: number;
            referencedApps: number;
            detectedRelations: number;
            riskCount: number;
            codeFileCount: number;
            obfuscatedFileCount: number;
          };
          risks: { level?: string; title?: string; reason?: string; recommendation?: string }[];
          dependencies: {
            impact?: string;
            type?: string;
            source?: string;
            key?: string;
            affectedFields?: number;
            reason?: string;
          }[];
          app_info_data: Record<string, unknown>;
        } = {
          selectedApps: [],
          summary: {
            referencingApps: 0,
            referencedApps: 0,
            detectedRelations: 0,
            riskCount: 0,
            codeFileCount: 0,
            obfuscatedFileCount: 0,
          },
          risks: [],
          dependencies: [],
          app_info_data: {},
        };

        // Try to extract specific sections using simpler regex
        try {
          // Extract selectedApps
          const appsMatch = content.match(/'selectedApps':\s*\[([^\]]*?)\]/);
          if (appsMatch?.[1]) {
            try {
              const appsStr = appsMatch[1]
                .replace(/None/g, "null")
                .replace(/True/g, "true")
                .replace(/False/g, "false")
                .replace(/'/g, '"');
              defaultData.selectedApps = JSON.parse(`[${appsStr}]`);
            } catch (_e) {
              console.error("Could not parse selectedApps");
            }
          }

          // Extract summary
          const summaryMatch = content.match(/'summary':\s*{([^}]*?)}/);
          if (summaryMatch?.[1]) {
            try {
              const summaryStr = summaryMatch[1]
                .replace(/None/g, "null")
                .replace(/True/g, "true")
                .replace(/False/g, "false")
                .replace(/'/g, '"');
              const summary = JSON.parse(`{${summaryStr}}`);
              defaultData.summary = { ...defaultData.summary, ...summary };
            } catch (_e) {
              console.error("Could not parse summary");
            }
          }

          // Try to extract risks array - improved version
          // Use more flexible pattern to handle both single and double quotes
          const risksMatch = content.match(/['"]?risks['"]?\s*:\s*\[([\s\S]*?)\](?:\s*,|\s*\})/);
          if (risksMatch?.[1]) {
            try {
              console.log("Found risks section, attempting to parse...");

              // First, clean up the risks section text
              const risksText = risksMatch[1]
                .replace(/None/g, "null")
                .replace(/True/g, "true")
                .replace(/False/g, "false");

              // Try direct parsing approach for risks
              // Look for risk objects with flexible pattern
              const riskPattern =
                /\{[^{}]*?['"]level['"]?\s*:\s*['"]?(HIGH|MEDIUM|LOW)['"]?[^{}]*?\}/gi;

              const riskMatches = risksText.match(riskPattern);
              const risks = [];

              if (riskMatches && riskMatches.length > 0) {
                // Parse each matched risk object
                for (const riskStr of riskMatches) {
                  try {
                    // Clean up the risk string for parsing
                    const cleanRisk = riskStr
                      .replace(/'/g, '"') // Replace single quotes with double quotes
                      .replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":') // Quote unquoted keys
                      .replace(/:\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*([,}])/g, ': "$1"$2'); // Quote unquoted string values

                    // Try to parse
                    const riskObj = JSON.parse(cleanRisk);
                    if (riskObj.level && riskObj.title) {
                      risks.push(riskObj);
                    }
                  } catch (_e) {
                    // If parsing fails, try to extract fields manually
                    const levelMatch = riskStr.match(
                      /['"]level['"]?\s*:\s*['"]?(HIGH|MEDIUM|LOW)['"]?/i,
                    );
                    const titleMatch = riskStr.match(/['"]title['"]?\s*:\s*['"]([^'"]+)['"]/);
                    const reasonMatch = riskStr.match(/['"]reason['"]?\s*:\s*['"]([^'"]+)['"]/);
                    const recommendationMatch = riskStr.match(
                      /['"]recommendation['"]?\s*:\s*['"]([^'"]+)['"]/,
                    );

                    if (levelMatch && titleMatch) {
                      risks.push({
                        level: levelMatch[1],
                        title: titleMatch[1],
                        reason: reasonMatch ? reasonMatch[1] : "",
                        recommendation: recommendationMatch ? recommendationMatch[1] : "",
                      });
                    }
                  }
                }

                if (risks.length > 0) {
                  console.log(`Extracted ${risks.length} risks using pattern matching`);
                  defaultData.risks = risks;
                }
              } else {
                // Fallback: try to extract with simpler pattern
                const simplifiedRisks = risksMatch[1].match(/\{[^{}]*\}/g);
                if (simplifiedRisks) {
                  defaultData.risks = simplifiedRisks
                    .map((riskStr) => {
                      try {
                        // More aggressive cleaning
                        const cleanRisk = riskStr
                          .replace(/\\'/g, "'") // Handle escaped quotes
                          .replace(/\\"/g, '"') // Handle escaped double quotes
                          .replace(/\\\\/g, "\\") // Handle escaped backslashes
                          .replace(/None/g, "null")
                          .replace(/True/g, "true")
                          .replace(/False/g, "false")
                          .replace(/'([^']+)':/g, '"$1":') // Replace 'key': with "key":
                          .replace(/:\s*'([^']+)'/g, ': "$1"'); // Replace : 'value' with : "value"
                        return JSON.parse(cleanRisk);
                      } catch (e) {
                        console.error("Failed to parse individual risk:", e);
                        return null;
                      }
                    })
                    .filter((risk) => risk !== null);
                }
              }
            } catch (e) {
              console.error("Could not parse risks:", e);
            }
          } else {
            console.log("No risks section found in content");
          }

          // Try to extract dependencies array - improved version
          // Use more flexible pattern
          const depsMatch = content.match(
            /['"]?dependencies['"]?\s*:\s*\[([\s\S]*?)\](?:\s*,|\s*\})/,
          );
          if (depsMatch?.[1]) {
            try {
              console.log("Found dependencies section, attempting to parse...");

              // Try to extract with pattern matching first
              const depPattern =
                /\{\s*['"]impact['"]\s*:\s*['"]([^'"]+)['"]\s*,\s*['"]type['"]\s*:\s*['"]([^'"]+)['"]\s*,\s*['"]source['"]\s*:\s*['"]([^'"]+)['"]\s*,\s*['"]key['"]\s*:\s*['"]([^'"]+)['"]\s*,\s*['"]affectedFields['"]\s*:\s*(\d+)\s*,\s*['"]reason['"]\s*:\s*['"]([^'"]+)['"]\s*\}/g;

              let depMatch;
              const deps: typeof defaultData.dependencies = [];
              const depsText = depsMatch[1]
                .replace(/None/g, "null")
                .replace(/True/g, "true")
                .replace(/False/g, "false");

              while ((depMatch = depPattern.exec(depsText)) !== null) {
                deps.push({
                  impact: depMatch[1] ?? "",
                  type: depMatch[2] ?? "",
                  source: depMatch[3] ?? "",
                  key: depMatch[4] ?? "",
                  affectedFields: depMatch[5] ? parseInt(depMatch[5], 10) : 0,
                  reason: depMatch[6] ?? "",
                });
              }

              if (deps.length > 0) {
                console.log(`Extracted ${deps.length} dependencies`);
                defaultData.dependencies = deps;
              } else {
                // Fallback: try simpler extraction
                const simplifiedDeps = depsMatch[1].match(/\{[^{}]*\}/g);
                if (simplifiedDeps) {
                  defaultData.dependencies = simplifiedDeps
                    .map((depStr) => {
                      try {
                        // More aggressive cleaning
                        const cleanDep = depStr
                          .replace(/\\'/g, "'") // Handle escaped quotes
                          .replace(/\\"/g, '"') // Handle escaped double quotes
                          .replace(/\\\\/g, "\\") // Handle escaped backslashes
                          .replace(/None/g, "null")
                          .replace(/True/g, "true")
                          .replace(/False/g, "false")
                          .replace(/'([^']+)':/g, '"$1":') // Replace 'key': with "key":
                          .replace(/:\s*'([^']+)'/g, ': "$1"') // Replace : 'value' with : "value"
                          .replace(/:\s*(\d+)([,}])/g, ": $1$2"); // Ensure numbers are unquoted
                        return JSON.parse(cleanDep);
                      } catch (e) {
                        console.error("Failed to parse individual dependency:", e);
                        return null;
                      }
                    })
                    .filter((dep) => dep !== null);
                }
              }
            } catch (e) {
              console.error("Could not parse dependencies:", e);
            }
          } else {
            console.log("No dependencies section found in content");
          }
        } catch (_e) {
          console.error("Could not extract data sections");
        }

        return defaultData;
      }
    }
  }, []);

  // Parse dependency data from document - memoized to prevent re-parsing on tab switch
  const dependencyData = useMemo(() => {
    if (!dependencyDocument) return null;

    try {
      // Check if content is a validation error message
      if (
        dependencyDocument.content &&
        (dependencyDocument.content.includes("validation error") ||
          dependencyDocument.content.startsWith("1 validation error"))
      ) {
        console.warn("Dependency document contains validation error:", dependencyDocument.content);
        // Create default structure with empty data
        return {
          selectedApps: [],
          summary: {
            referencingApps: 0,
            referencedApps: 0,
            detectedRelations: 0,
            riskCount: 0,
            codeFileCount: 0,
            obfuscatedFileCount: 0,
          },
          risks: [],
          dependencies: [],
          app_info_data: {},
        };
      } else {
        // Check if content appears to be truncated
        const content = dependencyDocument.content;
        let parsedData;
        if (content?.endsWith("...")) {
          console.warn("Content appears to be truncated");
          // Try to add closing braces to make it valid JSON
          const fixedContent = `${content.slice(0, -3)}}}`;
          parsedData = parsePythonJSON(fixedContent);
        } else {
          parsedData = parsePythonJSON(content);
        }

        // Ensure we have the required structure
        if (!parsedData || typeof parsedData !== "object") {
          throw new Error("Invalid dependency data structure");
        }

        console.log("Parsed dependency data:", parsedData);
        return parsedData;
      }
    } catch (e) {
      console.error("Failed to parse dependency document:", e);
      console.error("Content length:", dependencyDocument.content?.length);
      console.error("Content last 100 chars:", dependencyDocument.content?.slice(-100));
      // Create default structure on parse error
      return {
        selectedApps: [],
        summary: {
          referencingApps: 0,
          referencedApps: 0,
          detectedRelations: 0,
          riskCount: 0,
          codeFileCount: 0,
          obfuscatedFileCount: 0,
        },
        risks: [],
        dependencies: [],
      };
    }
  }, [dependencyDocument, parsePythonJSON]);

  // Parse requirements data from document - memoized to prevent re-parsing on tab switch
  const requirementsData = useMemo(() => {
    if (!requirementsDocument) return null;

    try {
      // Check if content is a validation error message
      if (
        requirementsDocument.content &&
        (requirementsDocument.content.includes("validation error") ||
          requirementsDocument.content.startsWith("1 validation error"))
      ) {
        console.warn(
          "Requirements document contains validation error:",
          requirementsDocument.content,
        );
        // Create default structure with empty data
        return {
          systemPurpose: "",
          apps: [],
        };
      } else {
        const parsed = parsePythonJSON(requirementsDocument.content);

        // データが配列の場合は、systemPurposeとappsを含む形式に変換
        if (Array.isArray(parsed)) {
          return {
            systemPurpose: "", // デフォルトの目的文
            apps: parsed,
          };
        } else if (parsed && typeof parsed === "object") {
          // すでに正しい形式の場合
          return {
            systemPurpose: parsed.systemPurpose || "",
            apps: parsed.apps || [],
          };
        } else {
          return {
            systemPurpose: "",
            apps: [],
          };
        }
      }
    } catch (e) {
      console.error("Failed to parse requirements document:", e);
      console.error("Content:", requirementsDocument.content);
      return {
        systemPurpose: "",
        apps: [],
      };
    }
  }, [requirementsDocument, parsePythonJSON]);

  // Parse ER diagram data from document - memoized to prevent re-parsing on tab switch
  const erDiagramData = useMemo(() => {
    if (!erDocument) return null;

    try {
      // Check if content is a validation error message
      if (
        erDocument.content &&
        (erDocument.content.includes("validation error") ||
          erDocument.content.startsWith("1 validation error"))
      ) {
        console.warn("ER diagram document contains validation error:", erDocument.content);
        // Create default structure with empty data
        return [];
      } else {
        const parsed = parsePythonJSON(erDocument.content);
        return parsed;
      }
    } catch (e) {
      console.error("Failed to parse ER diagram document:", e);
      return [];
    }
  }, [erDocument, parsePythonJSON]);

  const hasContent = (value: unknown): value is Record<string, any> => {
    return (
      typeof value === "object" &&
      value !== null &&
      Object.keys(value as Record<string, any>).length > 0
    );
  };

  const dependencyContent = hasContent(reportData?.dependencyAnalysis)
    ? (reportData?.dependencyAnalysis as any)
    : dependencyData;

  const detailAppIds = useMemo(() => {
    if (!dependencyContent) {
      return [] as string[];
    }
    const raw = dependencyContent?.detailAppIds ?? dependencyContent?.detail_app_ids ?? [];
    if (Array.isArray(raw)) {
      return raw.map((id: any) => String(id));
    }
    return [] as string[];
  }, [dependencyContent]);

  const filterAppsForDetail = useCallback(
    (apps: any[] | undefined) => {
      if (!apps || !Array.isArray(apps) || apps.length === 0) {
        return [];
      }

      if (detailAppIds.length > 0) {
        const targetIds = new Set(detailAppIds.map((id) => String(id)));
        return apps.filter((app) => {
          const appId = (app as any)?.id ?? (app as any)?.appId;
          return appId ? targetIds.has(String(appId)) : false;
        });
      }

      const flagged = apps.filter((app: any) => app?.isDetailAnalysisTarget);
      return flagged.length ? flagged : apps;
    },
    [detailAppIds],
  );

  const requirementsContent = hasContent(reportData?.requirementsAnalysis)
    ? (reportData?.requirementsAnalysis as any)
    : requirementsData;

  // Memoize selectedApps to prevent re-creating the array on every render
  // Must be called before early returns to follow Rules of Hooks
  const dependencySelectedApps = useMemo(() => {
    if (!reportData) return [];

    // 依存関係分析データのselectedAppsがある場合、iconFileKeyを補完
    if (dependencyContent?.selectedApps && Array.isArray(dependencyContent.selectedApps)) {
      const baseApps = dependencyContent.selectedApps.map((app: any) => {
        const appId = app.id || app.appId;

        // app_info_dataから詳細情報を取得（iconFileKeyを含む）
        const appInfoData = dependencyContent?.app_info_data?.[appId];

        // reportDataから該当するアプリの情報を探して補完
        const kintoneApp = reportData?.kintone_apps?.find((ka: any) => ka.appId === appId);

        return {
          ...app,
          id: appId,
          name: app.name || appInfoData?.name || kintoneApp?.name || "",
          description: app.description || appInfoData?.description || kintoneApp?.description || "",
          iconFileKey: appInfoData?.iconFileKey || null,
        };
      });

      return filterAppsForDetail(baseApps);
    }

    // それ以外はkintone_appsまたはselectedAppsを使用
    const kintoneApps =
      reportData?.kintone_apps?.map((app) => ({
        id: app.appId,
        name: app.name,
        description: app.description || "",
        iconFileKey: app.iconFileKey ?? null,
      })) || [];
    const baseApps = reportData?.selectedApps || kintoneApps;
    return filterAppsForDetail(baseApps);
  }, [
    reportData,
    dependencyContent?.selectedApps,
    dependencyContent?.app_info_data,
    filterAppsForDetail,
  ]);

  const dependencyAppsForDisplay = dependencySelectedApps;

  const erDiagramSelectedApps = useMemo(() => {
    if (!reportData) return [];
    const kintoneApps =
      reportData?.kintone_apps?.map((app) => ({
        id: app.appId,
        name: app.name,
        iconFileKey: app.iconFileKey ?? null,
      })) || [];
    return reportData?.selectedApps || kintoneApps;
  }, [reportData]);

  const erDiagramAppsForDisplay = useMemo(() => {
    return filterAppsForDetail(erDiagramSelectedApps);
  }, [erDiagramSelectedApps, filterAppsForDetail]);

  // Configure tabs - 依存関係分析/ER図/要件分析の3タブのみ
  const tabs = useMemo(
    () => [
      {
        label: "依存関係分析",
        content: (
          <DependencyAnalysisResult
            selectedApps={dependencyAppsForDisplay}
            detailAppIds={detailAppIds}
            summary={dependencyContent?.summary}
            risks={dependencyContent?.risks || []}
            dependencies={dependencyContent?.dependencies || []}
            projectName={currentProject?.name}
            documentId={documents.find((doc) => doc.document_type === "dependency-overview")?.id}
            report={reportData}
          />
        ),
      },
      {
        label: "ER図",
        content: (
          <ERDiagram
            selectedApps={erDiagramAppsForDisplay}
            diagramData={erDiagramData}
            isOtherTabEditing={isAnyTabEditing}
            onEditingChange={setIsAnyTabEditing}
          />
        ),
      },
      {
        label: "要件分析",
        content: (
          <RequirementsAnalysisResult
            data={requirementsContent || undefined}
            projectName={currentProject?.name}
            documentId={documents.find((doc) => doc.document_type === "requirement-analysis")?.id}
            isOtherTabEditing={isAnyTabEditing}
            onEditingChange={setIsAnyTabEditing}
            report={reportData}
          />
        ),
      },
    ],
    [
      dependencyAppsForDisplay,
      detailAppIds,
      dependencyContent,
      currentProject?.name,
      documents,
      reportData,
      erDiagramAppsForDisplay,
      erDiagramData,
      isAnyTabEditing,
      requirementsContent,
    ],
  );

  if (isLoading) {
    return <div className={styles["loading"]}>読み込み中...</div>;
  }

  if (!reportData) {
    return null;
  }

  return (
    <div className={styles["container"]}>
      <div className={styles["headerWrapper"]}>
        {isEditingTitle ? (
          <div className={styles["titleEditContainer"]}>
            <input
              type="text"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              className={styles["titleInput"]}
              placeholder="レポート名を入力"
            />
            <div className={styles["titleEditButtons"]}>
              <button onClick={handleSaveTitle} className={styles["saveButton"]}>
                保存
              </button>
              <button onClick={handleCancelEdit} className={styles["cancelButton"]}>
                キャンセル
              </button>
            </div>
          </div>
        ) : (
          <CommonDocumentHeader
            title={
              reportData.name ||
              `分析レポート_${(() => {
                const dateVal = reportData.createdAt ?? reportData.created_at;
                return dateVal
                  ? new Date(dateVal).toLocaleDateString("ja-JP").replace(/\//g, "")
                  : new Date().toLocaleDateString("ja-JP").replace(/\//g, "");
              })()}`
            }
            documentType="app-design"
            createdAt={(() => {
              const dateVal = reportData.createdAt ?? reportData.created_at;
              if (!dateVal) return new Date().toISOString();
              return typeof dateVal === "object" && dateVal instanceof Date
                ? dateVal.toISOString()
                : new Date(dateVal).toISOString();
            })()}
            modifiedAt={(() => {
              const dateVal =
                reportData.updatedAt ??
                reportData.modified_at ??
                reportData.createdAt ??
                reportData.created_at;
              if (!dateVal) return new Date().toISOString();
              return typeof dateVal === "object" && dateVal instanceof Date
                ? dateVal.toISOString()
                : new Date(dateVal).toISOString();
            })()}
            modifiedBy={{
              display_name:
                (reportData.updatedBy && typeof reportData.updatedBy === "object"
                  ? reportData.updatedBy.display_name
                  : null) ||
                (reportData.updated_by && typeof reportData.updated_by === "object"
                  ? reportData.updated_by.display_name
                  : null) ||
                (reportData.createdBy && typeof reportData.createdBy === "object"
                  ? reportData.createdBy.display_name
                  : null) ||
                (reportData.created_by && typeof reportData.created_by === "object"
                  ? reportData.created_by.display_name
                  : null) ||
                "システム",
            }}
            isEditable={false}
            isLoading={isLoading}
            isHistory={false}
            handleHistoryClick={handleHistoryClick}
            handleBackClick={() => navigate(-1)}
            showEditButton={false}
            showExportButton={false}
          />
        )}

        {/* Context Menu Button */}
        <div className={styles["contextMenuWrapper"]}>
          <button
            className={styles["contextMenuButton"]}
            onClick={(e) => {
              e.preventDefault();
              setShowContextMenu(!showContextMenu);
            }}
          >
            <ContextMenu />
          </button>
          {showContextMenu && (
            <div className={styles["menu"]}>
              <button onClick={handleEditTitle}>編集</button>
            </div>
          )}
        </div>
      </div>

      <div className={styles["content"]}>
        <CommonTabs
          tabs={tabs}
          selectedIndex={selectedIndex}
          onSelect={handleTabSelect}
          disabled={isAnyTabEditing || isEditingTitle}
        />
      </div>
    </div>
  );
};

export default ReverseEngineeringDetail;
